import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  TextField,
  Typography,
  Checkbox,
  makeStyles,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { SimpleLambdaListEditor, omitDatadogLambdaApmFromLambdas } from 'config-editor';
import { LambdaGroup, type Lambda } from 'config-editor-common';
import Editor from '@monaco-editor/react';
import type { OnValidate } from '@monaco-editor/react';

type UiOptions = {
  definitionFieldName?: string;
};

type AdvancedOptions = {
  definition_template_vars?: Record<string, unknown>;
  lambda_integration_arns?: string[];
  managed_lambdas?: LambdaGroup;
  alarms?: {
    enabled?: boolean;
    map_state_names?: string[];
    sns_topic_names?: string[];
  };
};

type Props = FieldExtensionComponentProps<AdvancedOptions, UiOptions>;

const DEFAULT_DEFINITION_FIELD = 'step_function_definition_json';

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
}));

/** Step Function `managed_lambdas` in form state: `{ iam_policy_names, lambdas }` only (same as config editor). */
function normalizeAdvancedOptions(value: unknown): Required<AdvancedOptions> {
  const base: Required<AdvancedOptions> = {
    definition_template_vars: {},
    lambda_integration_arns: [],
    managed_lambdas: {
      iam_policy_names: [],
      lambdas: {},
    },
    alarms: {
      enabled: false,
      map_state_names: [],
      sns_topic_names: [],
    },
  };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return base;
  const v = value as Record<string, any>;
  return {
    definition_template_vars:
      v.definition_template_vars && typeof v.definition_template_vars === 'object'
        ? v.definition_template_vars
        : base.definition_template_vars,
    lambda_integration_arns: Array.isArray(v.lambda_integration_arns)
      ? v.lambda_integration_arns.map((x: unknown) => String(x))
      : base.lambda_integration_arns,
    managed_lambdas:
      v.managed_lambdas && typeof v.managed_lambdas === 'object'
        ? {
            iam_policy_names: Array.isArray(v.managed_lambdas.iam_policy_names)
              ? v.managed_lambdas.iam_policy_names.map((x: unknown) => String(x))
              : [],
            lambdas:
              v.managed_lambdas.lambdas && typeof v.managed_lambdas.lambdas === 'object'
                ? omitDatadogLambdaApmFromLambdas(
                    v.managed_lambdas.lambdas as Record<string, Lambda>,
                  )
                : {},
          }
        : base.managed_lambdas,
    alarms:
      v.alarms && typeof v.alarms === 'object'
        ? {
            enabled: Boolean(v.alarms.enabled),
            map_state_names: Array.isArray(v.alarms.map_state_names)
              ? v.alarms.map_state_names.map((x: unknown) => String(x))
              : [],
            sns_topic_names: Array.isArray(v.alarms.sns_topic_names)
              ? v.alarms.sns_topic_names.map((x: unknown) => String(x))
              : [],
          }
        : base.alarms,
  };
}

function extractLambdaArnsFromText(text: string): string[] {
  const matches = Array.from(
    text.matchAll(/arn:aws:lambda:[^"'\s]+:function:[A-Za-z0-9-_]+(?::[^"'\s]+)?/g),
  ).map(m => m[0]);
  return [
    ...new Set(
      matches.map(m =>
        m.replace(/(arn:aws:lambda:[^"'\s]+:function:[A-Za-z0-9-_]+)(?::[^"'\s]+)?$/, '$1'),
      ),
    ),
  ];
}

function parseDistributedMapLabels(definitionText: string): {
  labels: string[];
  parseError?: string;
} {
  try {
    const parsed = JSON.parse(definitionText);
    const states = parsed?.States;
    if (!states || typeof states !== 'object') return { labels: [] };

    const labels: string[] = [];
    for (const [stateName, stateDef] of Object.entries(states as Record<string, any>)) {
      if (!stateDef || typeof stateDef !== 'object') continue;
      if (stateDef.Type !== 'Map') continue;
      const mode = stateDef?.ItemProcessor?.ProcessorConfig?.Mode;
      if (mode !== 'DISTRIBUTED') continue;
      const label =
        typeof stateDef.Label === 'string' && stateDef.Label.trim()
          ? stateDef.Label.trim()
          : String(stateName);
      labels.push(label);
    }
    return { labels: [...new Set(labels)] };
  } catch (e) {
    return { labels: [], parseError: e instanceof Error ? e.message : String(e) };
  }
}

export const StepFunctionAdvancedOptionsEditor = (props: Props) => {
  const { onChange, formData, formContext, uiSchema, schema } = props;
  const classes = useStyles();

  const definitionFieldName =
    uiSchema?.['ui:options']?.definitionFieldName ?? DEFAULT_DEFINITION_FIELD;
  const rootFormData = (formContext as any)?.formData as Record<string, unknown> | undefined;
  const definitionText = String(rootFormData?.[definitionFieldName] ?? '');

  const [value, setValue] = useState<Required<AdvancedOptions>>(() =>
    normalizeAdvancedOptions(formData),
  );
  const [varsText, setVarsText] = useState(JSON.stringify(value.definition_template_vars, null, 2));
  const [varsErrors, setVarsErrors] = useState<string[]>([]);

  useEffect(() => {
    const next = normalizeAdvancedOptions(formData);
    setValue(next);
    setVarsText(JSON.stringify(next.definition_template_vars, null, 2));
    setVarsErrors([]);
  }, [formData]);

  const handleVarsValidate: OnValidate = markers => {
    setVarsErrors(
      markers.map(
        marker =>
          `Code ${marker.code} | ${marker.message} [Ln ${marker.startLineNumber}, Col ${marker.startColumn}]`,
      ),
    );
  };

  const lambdaDetected = useMemo(
    () => (definitionText.trim() ? extractLambdaArnsFromText(definitionText) : []),
    [definitionText],
  );
  const mapLabelsDetected = useMemo(
    () => (definitionText.trim() ? parseDistributedMapLabels(definitionText) : { labels: [] }),
    [definitionText],
  );
  const userTouchedLambdaRef = useRef(false);
  const prevLambdaDetectedRef = useRef('');

  useEffect(() => {
    const detectedKey = lambdaDetected.slice().sort().join('\n');
    if (detectedKey === prevLambdaDetectedRef.current) return;
    prevLambdaDetectedRef.current = detectedKey;
    if (userTouchedLambdaRef.current) return;
    const current = value.lambda_integration_arns ?? [];
    if (current.length === 0 && lambdaDetected.length > 0) {
      const next = { ...value, lambda_integration_arns: lambdaDetected };
      setValue(next);
      onChange(next);
      return;
    }
    if (current.length > 0 && lambdaDetected.length > 0) {
      const merged = [...new Set([...current, ...lambdaDetected])];
      if (merged.length !== current.length) {
        const next = { ...value, lambda_integration_arns: merged };
        setValue(next);
        onChange(next);
      }
    }
  }, [lambdaDetected, onChange, value]);

  const update = (partial: Partial<Required<AdvancedOptions>>) => {
    const next = { ...value, ...partial };
    setValue(next);
    onChange(next);
  };

  const title = schema.title || uiSchema?.['ui:title'] || 'Advanced Options';
  const description =
    schema.description ||
    uiSchema?.['ui:description'] ||
    'Configure advanced Step Function options';

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h6">{title}</Typography>
        {description && <FormHelperText>{description}</FormHelperText>}
      </Box>

      <Paper className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Definition Template Vars
        </Typography>
        {varsErrors.length > 0 && (
          <Alert severity="error" style={{ marginBottom: 8 }}>
            Fix the following JSON errors:
            <ul style={{ margin: '6px 0 0 18px' }}>
              {varsErrors.map(err => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}
        <FormHelperText style={{ marginBottom: 8 }}>
          JSON object passed to templatefile() as extra vars.
        </FormHelperText>
        <Editor
          height="240px"
          defaultLanguage="json"
          value={varsText}
          onValidate={handleVarsValidate}
          onChange={newValue => {
            if (newValue === undefined) return;
            setVarsText(newValue);
            try {
              const parsed = JSON.parse(newValue);
              if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
              update({ definition_template_vars: parsed as Record<string, unknown> });
            } catch {
              // Keep editor responsive while user types invalid JSON.
            }
          }}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            formatOnPaste: true,
            formatOnType: false,
            tabSize: 2,
          }}
        />
      </Paper>

      <Paper className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Invoke Permissions For Lambdas
        </Typography>
        <FormHelperText>
          List the Lambda ARNs your Step Function invokes so Terraform can generate the required
          invoke permissions.
        </FormHelperText>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={value.lambda_integration_arns}
          onChange={(_e, newValue) => {
            userTouchedLambdaRef.current = true;
            update({
              lambda_integration_arns: (newValue ?? []).map(v => String(v)).filter(Boolean),
            });
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="arn:aws:lambda:${region}:${account_id}:function:my_lambda"
            />
          )}
        />
      </Paper>

      <Paper className={classes.section}>
        <SimpleLambdaListEditor
          lambdas={value.managed_lambdas?.lambdas ?? {}}
          sharedIamPolicyNames={value.managed_lambdas?.iam_policy_names ?? []}
          onSharedIamPolicyNamesChange={policies =>
            update({
              managed_lambdas: {
                iam_policy_names: policies,
                lambdas: value.managed_lambdas?.lambdas ?? {},
              },
            })
          }
          onChange={lambdas =>
            update({
              managed_lambdas: {
                iam_policy_names: value.managed_lambdas?.iam_policy_names ?? [],
                lambdas,
              },
            })
          }
          title="Managed Lambdas"
          description="Use Add Lambda to create functions in this stack. The Step Function is automatically allowed to invoke lambdas configured here."
          emptyStateTitle="No managed lambdas configured"
          emptyStateDescription="Add your first managed lambda to this Step Function stack."
          emptyStateAddButtonText="Add First Lambda"
          showPaper={false}
        />
      </Paper>

      <Paper className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          MapRun Alarms
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value.alarms?.enabled)}
              onChange={e =>
                update({
                  alarms: {
                    ...(value.alarms ?? {}),
                    enabled: e.target.checked,
                  },
                })
              }
              color="primary"
            />
          }
          label="Enable MapRun Alarms"
        />
        {definitionText.trim() && mapLabelsDetected.parseError && (
          <Alert severity="info" style={{ marginBottom: 8 }}>
            Unable to auto-detect MapRun labels ({mapLabelsDetected.parseError}).
          </Alert>
        )}
        <FormControl fullWidth style={{ marginBottom: 12 }}>
          <Autocomplete
            multiple
            freeSolo
            options={mapLabelsDetected.labels}
            value={value.alarms?.map_state_names ?? []}
            onChange={(_e, newValue) =>
              update({
                alarms: {
                  ...(value.alarms ?? {}),
                  map_state_names: (newValue ?? []).map(v => String(v)).filter(Boolean),
                },
              })
            }
            disabled={!value.alarms?.enabled}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Map State Labels"
                placeholder="FileAnalysis"
              />
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={value.alarms?.sns_topic_names ?? []}
            onChange={(_e, newValue) =>
              update({
                alarms: {
                  ...(value.alarms ?? {}),
                  sns_topic_names: (newValue ?? []).map(v => String(v)).filter(Boolean),
                },
              })
            }
            disabled={!value.alarms?.enabled}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Alarm SNS Topic Names"
                placeholder="lexipub-cw-alarms-topic"
              />
            )}
          />
        </FormControl>
      </Paper>
    </Box>
  );
};
